import { type OrderbookEntry } from "#/types/orderbook";

interface ExecPriceOrderbook {
  bids?: OrderbookEntry[];
  asks?: OrderbookEntry[];
  isBuy: boolean;
}

interface ExecPriceOrderbookSide {
  depth: [number, number][]; // [price, size]
  isBuy: boolean;
}

type ExecPriceValues = ExecPriceOrderbook | ExecPriceOrderbookSide;

const isOrderbookInput = (
  input: ExecPriceValues,
): input is ExecPriceOrderbook => {
  return "bids" in input && "asks" in input && "isBuy" in input;
};

export interface CalculatedExecPrice {
  hasEnoughLiquidity: boolean; // totalSize >= size;
  executionPrice: number; // weighted calculation
}

export const execPriceDefaultValues: CalculatedExecPrice = {
  executionPrice: NaN,
  hasEnoughLiquidity: false,
};

export const calculateEstimatedExecutionPrice = (
  usersSize: number,
  values?: ExecPriceValues,
) => {
  if (usersSize === 0 || !values) return execPriceDefaultValues;

  const depthOfMarket = isOrderbookInput(values)
    ? values[values.isBuy ? "asks" : "bids"]
    : values.depth?.map(([price, size]) => ({ price, size }) as OrderbookEntry);

  const sortedDepthOfMarket = depthOfMarket?.toSorted((a, b) =>
    values.isBuy ? a.price - b.price : b.price - a.price,
  );

  if (!sortedDepthOfMarket) return execPriceDefaultValues;

  const totalSize = sortedDepthOfMarket.reduce(
    (acc: number, curr: OrderbookEntry) => acc + curr.size,
    0,
  );

  if (totalSize < usersSize) return execPriceDefaultValues;

  const { price, size } = sortedDepthOfMarket.reduce(
    (acc: { size: number; price: number }, curr: OrderbookEntry) => {
      if (acc.size >= usersSize) return acc;

      const requiredSize = Math.min(curr.size, usersSize - acc.size);

      acc.size += requiredSize;
      acc.price += requiredSize * curr.price;
      return acc;
    },
    { size: 0, price: 0 },
  );

  const weightedAveragePrice = size > 0 ? price / size : NaN;

  return {
    executionPrice: weightedAveragePrice,
    hasEnoughLiquidity: true,
  };
};
